import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Calendar, MapPin, FileText, 
  Download, Eye, CheckCircle, Clock, XCircle,
  User, Building, DollarSign, Truck
} from 'lucide-react';
import toast from 'react-hot-toast';
import { 
  maskClientName, 
  maskCompanyName, 
  maskPhone, 
  maskCEP,
  shouldShowFullClientData,
  getClientDisplayData
} from '../utils/dataProtection';

interface Pedido {
  id: number;
  cliente: {
    nome: string;
    empresa: string;
    email: string;
    telefone: string;
    cep: string;
  };
  tipo_equipamento: string;
  marca: string;
  modelo: string;
  tipo_peca: string;
  descricao: string;
  status: string;
  data_criacao: string;
  prazo: string;
  valor_estimado?: number;
  imagem?: string;
  especificacoes?: string;
  orcamento_enviado?: boolean;
  orcamento?: {
    valor: number;
    condicao_peca: string;
    garantia: string;
    tempo_entrega: string;
    frete_incluso: boolean;
    observacoes: string;
    arquivo?: string;
    data_envio: string;
  };
  fornecedor_escolhido_id?: number;
}

const PedidoDetalhesFornecedor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentFornecedorId] = useState(1); // ID do fornecedor logado

  useEffect(() => {
    loadPedido();
  }, [id]);

  const getClientDisplayInfo = (pedido: Pedido) => {
    const shouldShowFullData = shouldShowFullClientData(
      pedido.status, 
      currentFornecedorId, 
      pedido.fornecedor_escolhido_id
    );
    
    return getClientDisplayData(pedido.cliente, !shouldShowFullData);
  };

  const loadPedido = async () => {
    try {
      // Simular carregamento de dados
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Dados baseados no ID do pedido
      const pedidosData: { [key: number]: Pedido } = {
        1: {
          id: 1,
          cliente: {
            nome: 'João Silva',
            empresa: 'Empresa ABC Ltda',
            email: 'joao@empresaabc.com',
            telefone: '(11) 99999-9999',
            cep: '01234-567'
          },
          tipo_equipamento: 'Escavadeira',
          marca: 'Caterpillar',
          modelo: '320D',
          tipo_peca: 'Motor',
          descricao: 'Motor completo para escavadeira Caterpillar 320D. Inclui pistões, cilindros, cabeçote e sistema de injeção. Peça original ou equivalente de qualidade.',
          status: 'disponivel',
          data_criacao: '2024-01-15',
          prazo: '2024-01-20',
          valor_estimado: 15000,
          especificacoes: 'Motor diesel 4 cilindros, 6.8L, 180 HP. Compatível com modelos 320D, 320DL, 320DLN.'
        },
        2: {
          id: 2,
          cliente: {
            nome: 'Maria Santos',
            empresa: 'Construções XYZ',
            email: 'maria@construcoesxyz.com',
            telefone: '(11) 88888-8888',
            cep: '04567-890'
          },
          tipo_equipamento: 'Pá Carregadeira',
          marca: 'Komatsu',
          modelo: 'WA380',
          tipo_peca: 'Hidráulica',
          descricao: 'Sistema hidráulico completo para pá carregadeira. Inclui bombas, válvulas e cilindros.',
          status: 'em_andamento',
          data_criacao: '2024-01-14',
          prazo: '2024-01-19',
          valor_estimado: 8500,
          orcamento_enviado: true,
          orcamento: {
            valor: 8200,
            condicao_peca: 'Peça original',
            garantia: '3 meses',
            tempo_entrega: '10 dias',
            frete_incluso: true,
            observacoes: 'Frete incluso para SP/RJ',
            data_envio: '2024-01-14'
          },
          especificacoes: 'Sistema hidráulico completo com pressão de trabalho 350 bar. Inclui bombas, válvulas direcionais e cilindros.'
        },
        3: {
          id: 3,
          cliente: {
            nome: 'Pedro Oliveira',
            empresa: 'Mineração 123',
            email: 'pedro@mineracao123.com',
            telefone: '(11) 77777-7777',
            cep: '07890-123'
          },
          tipo_equipamento: 'Trator',
          marca: 'John Deere',
          modelo: '750J',
          tipo_peca: 'Transmissão',
          descricao: 'Transmissão completa para trator. Inclui caixa de marchas e diferencial.',
          status: 'aprovado',
          data_criacao: '2024-01-10',
          prazo: '2024-01-15',
          valor_estimado: 12000,
          orcamento_enviado: true,
          orcamento: {
            valor: 11800,
            condicao_peca: 'Peça original',
            garantia: '6 meses',
            tempo_entrega: '15 dias',
            frete_incluso: false,
            observacoes: 'Frete adicional para regiões distantes',
            data_envio: '2024-01-10'
          },
          fornecedor_escolhido_id: 1, // Este fornecedor foi escolhido
          especificacoes: 'Transmissão automática com 6 velocidades. Compatível com modelos 750J, 850J.'
        },
        4: {
          id: 4,
          cliente: {
            nome: 'Ana Costa',
            empresa: 'Construções Silva',
            email: 'ana@construcoessilva.com',
            telefone: '(11) 66666-6666',
            cep: '02345-678'
          },
          tipo_equipamento: 'Retroescavadeira',
          marca: 'Case',
          modelo: '580N',
          tipo_peca: 'Braço Hidráulico',
          descricao: 'Braço hidráulico completo para retroescavadeira. Inclui cilindros e válvulas.',
          status: 'finalizado',
          data_criacao: '2024-01-05',
          prazo: '2024-01-10',
          valor_estimado: 15000,
          orcamento_enviado: true,
          orcamento: {
            valor: 12500,
            condicao_peca: 'Peça original',
            garantia: '1 ano',
            tempo_entrega: '7 dias',
            frete_incluso: true,
            observacoes: 'Frete incluso para SP/RJ',
            data_envio: '2024-01-05'
          },
          especificacoes: 'Braço hidráulico com alcance de 6.5m. Inclui cilindros de elevação e extensão.'
        },
        5: {
          id: 5,
          cliente: {
            nome: 'Carlos Lima',
            empresa: 'Logística Express',
            email: 'carlos@logisticaexpress.com',
            telefone: '(11) 55555-5555',
            cep: '05678-901'
          },
          tipo_equipamento: 'Empilhadeira',
          marca: 'Toyota',
          modelo: 'FD150',
          tipo_peca: 'Motor Elétrico',
          descricao: 'Motor elétrico para empilhadeira. Potência 15kW, tensão 48V.',
          status: 'reprovado',
          data_criacao: '2024-01-08',
          prazo: '2024-01-13',
          valor_estimado: 12000,
          orcamento_enviado: true,
          orcamento: {
            valor: 13500,
            condicao_peca: 'Peça original',
            garantia: '6 meses',
            tempo_entrega: '10 dias',
            frete_incluso: false,
            observacoes: 'Frete adicional para regiões distantes',
            data_envio: '2024-01-08'
          },
          especificacoes: 'Motor elétrico trifásico, 15kW, 48V. Compatível com empilhadeiras Toyota FD150.'
        }
      };
      
      const pedidoId = parseInt(id || '1');
      const pedidoData = pedidosData[pedidoId] || pedidosData[1];
      setPedido(pedidoData);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do pedido');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'disponivel':
        return 'text-green-600 bg-green-100';
      case 'em_andamento':
        return 'text-blue-600 bg-blue-100';
      case 'aprovado':
        return 'text-purple-600 bg-purple-100';
      case 'reprovado':
        return 'text-red-600 bg-red-100';
      case 'finalizado':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'disponivel':
        return <Package className="w-4 h-4" />;
      case 'em_andamento':
        return <Clock className="w-4 h-4" />;
      case 'aprovado':
        return <CheckCircle className="w-4 h-4" />;
      case 'reprovado':
        return <XCircle className="w-4 h-4" />;
      case 'finalizado':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'disponivel':
        return 'Disponível';
      case 'em_andamento':
        return 'Em Andamento';
      case 'aprovado':
        return 'Aprovado';
      case 'reprovado':
        return 'Reprovado';
      case 'finalizado':
        return 'Finalizado';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando detalhes do pedido...</p>
        </div>
      </div>
    );
  }

  if (!pedido) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Pedido não encontrado</h2>
          <p className="text-gray-600 mb-4">O pedido solicitado não foi encontrado.</p>
          <button
            onClick={() => navigate('/fornecedor/dashboard')}
            className="btn btn-primary"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-red rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-gray-900">PeçaJá</span>
            </div>

            <button
              onClick={() => navigate('/fornecedor/dashboard')}
              className="btn btn-outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Dashboard
            </button>
          </div>
        </div>
      </header>

      <div className="container py-8">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <button
            onClick={() => navigate('/fornecedor/dashboard')}
            className="breadcrumb-item"
          >
            Dashboard
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Pedido #{pedido.id}</span>
        </div>

        {/* Pedido Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {pedido.tipo_equipamento} {pedido.marca} {pedido.modelo}
                  </h1>
                  <p className="text-lg text-gray-600">
                    <span className="font-medium">Peça:</span> {pedido.tipo_peca}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(pedido.status)}`}>
                    <div className="flex items-center space-x-1">
                      {getStatusIcon(pedido.status)}
                      <span>{getStatusText(pedido.status)}</span>
                    </div>
                  </span>
                </div>
              </div>

              <div className="space-y-6">
                {/* Descrição */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Descrição</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {pedido.descricao}
                  </p>
                </div>

                {/* Especificações */}
                {pedido.especificacoes && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Especificações</h3>
                    <p className="text-gray-600 leading-relaxed">
                      {pedido.especificacoes}
                    </p>
                  </div>
                )}

                {/* Seu Orçamento */}
                {pedido.orcamento_enviado && pedido.orcamento && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Seu Orçamento</h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <DollarSign className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium text-blue-900">
                              R$ {pedido.orcamento.valor.toLocaleString('pt-BR')}
                            </p>
                            <p className="text-sm text-blue-700">
                              Status: {getStatusText(pedido.status)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-blue-200">
                          <div>
                            <p className="text-sm text-blue-700 font-medium">Condição da Peça</p>
                            <p className="text-sm text-blue-800">{pedido.orcamento.condicao_peca}</p>
                          </div>
                          
                          <div>
                            <p className="text-sm text-blue-700 font-medium">Garantia</p>
                            <p className="text-sm text-blue-800">{pedido.orcamento.garantia}</p>
                          </div>
                          
                          <div>
                            <p className="text-sm text-blue-700 font-medium">Tempo de Entrega</p>
                            <p className="text-sm text-blue-800">{pedido.orcamento.tempo_entrega}</p>
                          </div>
                          
                          <div>
                            <p className="text-sm text-blue-700 font-medium">Frete Incluso</p>
                            <p className="text-sm text-blue-800">{pedido.orcamento.frete_incluso ? 'Sim' : 'Não'}</p>
                          </div>
                        </div>
                        
                        {pedido.orcamento.observacoes && (
                          <div className="pt-3 border-t border-blue-200">
                            <p className="text-sm text-blue-700 font-medium">Observações</p>
                            <p className="text-sm text-blue-800">{pedido.orcamento.observacoes}</p>
                          </div>
                        )}
                        
                        {pedido.orcamento.arquivo && (
                          <div className="pt-3 border-t border-blue-200">
                            <p className="text-sm text-blue-700 font-medium">Arquivo Anexado</p>
                            <a 
                              href={`/uploads/${pedido.orcamento.arquivo}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center"
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              {pedido.orcamento.arquivo}
                            </a>
                          </div>
                        )}
                        
                        <div className="pt-3 border-t border-blue-200">
                          <p className="text-sm text-blue-700 font-medium">Data de Envio</p>
                          <p className="text-sm text-blue-800">
                            {new Date(pedido.orcamento.data_envio).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Informações */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Data de Criação</p>
                      <p className="font-medium text-gray-900">
                        {new Date(pedido.data_criacao).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Prazo</p>
                      <p className="font-medium text-gray-900">
                        {new Date(pedido.prazo).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>

                  {pedido.valor_estimado && (
                    <div className="flex items-center space-x-3">
                      <DollarSign className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Valor Estimado</p>
                        <p className="font-medium text-gray-900">
                          R$ {pedido.valor_estimado.toLocaleString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">ID do Pedido</p>
                      <p className="font-medium text-gray-900">#{pedido.id}</p>
                    </div>
                  </div>
                </div>

                {/* Imagem */}
                {pedido.imagem && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Imagem</h3>
                    <div className="border rounded-lg p-4">
                      <img
                        src={pedido.imagem}
                        alt="Imagem do pedido"
                        className="w-full h-64 object-cover rounded"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Cliente Info */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações do Cliente</h3>
              <div className="space-y-4">
                {(() => {
                  const clientInfo = getClientDisplayInfo(pedido);
                  return (
                    <>
                      <div className="flex items-center space-x-3">
                        <User className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-500">Nome</p>
                          <p className="font-medium text-gray-900">{clientInfo.nome}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Building className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-500">Empresa</p>
                          <p className="font-medium text-gray-900">{clientInfo.empresa}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <MapPin className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-500">CEP</p>
                          <p className="font-medium text-gray-900">{clientInfo.cep}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-500">Telefone</p>
                          <p className="font-medium text-gray-900">{clientInfo.telefone}</p>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Actions */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações</h3>
              <div className="space-y-3">
                {pedido.status === 'disponivel' && (
                  <button
                    onClick={() => navigate(`/fornecedor/orcamento/${pedido.id}`)}
                    className="btn btn-primary w-full"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Enviar Orçamento
                  </button>
                )}
                
                <button
                  onClick={() => navigate('/fornecedor/dashboard')}
                  className="btn btn-outline w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar ao Dashboard
                </button>
              </div>
            </div>

            {/* Status Timeline */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Pedido Criado</p>
                    <p className="text-xs text-gray-500">
                      {new Date(pedido.data_criacao).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
                
                {pedido.status !== 'disponivel' && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Em Andamento</p>
                      <p className="text-xs text-gray-500">Fornecedores analisando</p>
                    </div>
                  </div>
                )}

                {pedido.status === 'em_andamento' && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Orçamento Enviado</p>
                      <p className="text-xs text-gray-500">Aguardando aprovação</p>
                    </div>
                  </div>
                )}

                {pedido.status === 'aprovado' && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Aprovado</p>
                      <p className="text-xs text-gray-500">Entre em contato com o cliente</p>
                    </div>
                  </div>
                )}

                {pedido.status === 'finalizado' && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Finalizado</p>
                      <p className="text-xs text-gray-500">Entrega concluída</p>
                    </div>
                  </div>
                )}

                {pedido.status === 'reprovado' && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Reprovado</p>
                      <p className="text-xs text-gray-500">Não foi selecionado</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PedidoDetalhesFornecedor; 